'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Play,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2,
  Download,
  Upload,
} from 'lucide-react';

export interface TestScenario {
  id: string;
  principal: string;
  action: string;
  resource: string;
  roles?: string[];
  expectedResult?: 'allow' | 'deny';
  actualResult?: 'allow' | 'deny' | null;
  status: 'pending' | 'running' | 'passed' | 'failed';
}

export interface BatchScenarioTesterProps {
  scenarios: TestScenario[];
  onScenariosChange: (scenarios: TestScenario[]) => void;
  onRunAll: () => Promise<void>;
  onRunSingle: (scenario: TestScenario) => Promise<{ allowed: boolean }>;
  isRunning?: boolean;
  availableResources?: string[];
  availableActions?: string[];
}

export function BatchScenarioTester({
  scenarios,
  onScenariosChange,
  onRunAll,
  onRunSingle,
  isRunning = false,
  availableResources = ['Matricks::Workflow', 'Matricks::Contact', 'Matricks::Provider', 'Matricks::AgentPipeline'],
  availableActions = ['read', 'write', 'delete', 'execute']
}: BatchScenarioTesterProps) {
  const [newScenario, setNewScenario] = useState<Partial<TestScenario>>({
    principal: '',
    action: 'read',
    resource: 'Matricks::Workflow',
    expectedResult: 'allow'
  });

  const addScenario = () => {
    if (!newScenario.principal) return;

    const scenario: TestScenario = {
      id: `scenario-${Date.now()}`,
      principal: newScenario.principal!,
      action: newScenario.action || 'read',
      resource: newScenario.resource || 'Matricks::Workflow',
      expectedResult: newScenario.expectedResult as 'allow' | 'deny',
      status: 'pending'
    };

    onScenariosChange([...scenarios, scenario]);
    setNewScenario({
      principal: '',
      action: 'read',
      resource: 'Matricks::Workflow',
      expectedResult: 'allow'
    });
  };

  const removeScenario = (id: string) => {
    onScenariosChange(scenarios.filter(s => s.id !== id));
  };

  const runSingleScenario = async (scenario: TestScenario) => {
    // Update status to running
    onScenariosChange(
      scenarios.map(s =>
        s.id === scenario.id ? { ...s, status: 'running' as const } : s
      )
    );

    try {
      const result = await onRunSingle(scenario);
      const actualResult = result.allowed ? 'allow' : 'deny';
      const passed = actualResult === scenario.expectedResult;

      onScenariosChange(
        scenarios.map(s =>
          s.id === scenario.id
            ? {
                ...s,
                actualResult,
                status: passed ? ('passed' as const) : ('failed' as const)
              }
            : s
        )
      );
    } catch {
      onScenariosChange(
        scenarios.map(s =>
          s.id === scenario.id ? { ...s, status: 'failed' as const } : s
        )
      );
    }
  };

  const clearResults = () => {
    onScenariosChange(
      scenarios.map(s => ({
        ...s,
        actualResult: null,
        status: 'pending' as const
      }))
    );
  };

  const passedCount = scenarios.filter(s => s.status === 'passed').length;
  const failedCount = scenarios.filter(s => s.status === 'failed').length;

  const exportScenarios = () => {
    const data = JSON.stringify(scenarios, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'test-scenarios.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Batch Testing</CardTitle>
            <CardDescription>
              Test multiple authorization scenarios at once
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {scenarios.length > 0 && (
              <>
                <Button variant="outline" size="sm" onClick={exportScenarios}>
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
                <Button variant="outline" size="sm" onClick={clearResults}>
                  Clear Results
                </Button>
              </>
            )}
            <Button onClick={onRunAll} disabled={scenarios.length === 0 || isRunning}>
              {isRunning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run All
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Results Summary */}
        {(passedCount > 0 || failedCount > 0) && (
          <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
            <span className="text-sm font-medium">Results:</span>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="h-3 w-3 mr-1" />
              {passedCount} passed
            </Badge>
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
              <XCircle className="h-3 w-3 mr-1" />
              {failedCount} failed
            </Badge>
            <span className="text-sm text-slate-500">
              {scenarios.length - passedCount - failedCount} pending
            </span>
          </div>
        )}

        {/* Add New Scenario Form */}
        <div className="flex items-end gap-2 p-4 bg-slate-50 rounded-lg border">
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium text-slate-500">Principal</label>
            <Input
              placeholder="user@example.com"
              value={newScenario.principal || ''}
              onChange={e => setNewScenario({ ...newScenario, principal: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="w-32 space-y-1">
            <label className="text-xs font-medium text-slate-500">Action</label>
            <Select
              value={newScenario.action}
              onValueChange={v => setNewScenario({ ...newScenario, action: v })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableActions.map(a => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48 space-y-1">
            <label className="text-xs font-medium text-slate-500">Resource</label>
            <Select
              value={newScenario.resource}
              onValueChange={v => setNewScenario({ ...newScenario, resource: v })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableResources.map(r => (
                  <SelectItem key={r} value={r}>{r.split('::')[1] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-28 space-y-1">
            <label className="text-xs font-medium text-slate-500">Expected</label>
            <Select
              value={newScenario.expectedResult}
              onValueChange={v => setNewScenario({ ...newScenario, expectedResult: v as 'allow' | 'deny' })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">Allow</SelectItem>
                <SelectItem value="deny">Deny</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={addScenario} disabled={!newScenario.principal} className="h-9">
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>

        {/* Scenarios Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Principal</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Expected</TableHead>
                <TableHead>Actual</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scenarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No test scenarios. Add scenarios above to test.
                  </TableCell>
                </TableRow>
              ) : (
                scenarios.map(scenario => (
                  <TableRow key={scenario.id}>
                    <TableCell className="font-mono text-sm">
                      {scenario.principal}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{scenario.action}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono text-xs">
                        {scenario.resource.split('::')[1] || scenario.resource}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          scenario.expectedResult === 'allow'
                            ? 'text-green-600 border-green-200'
                            : 'text-red-600 border-red-200'
                        }
                      >
                        {scenario.expectedResult}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {scenario.actualResult ? (
                        <Badge
                          variant="outline"
                          className={
                            scenario.actualResult === 'allow'
                              ? 'text-green-600 border-green-200 bg-green-50'
                              : 'text-red-600 border-red-200 bg-red-50'
                          }
                        >
                          {scenario.actualResult}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {scenario.status === 'running' ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      ) : scenario.status === 'passed' ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : scenario.status === 'failed' ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <span className="text-slate-400 text-xs">Pending</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => runSingleScenario(scenario)}
                          disabled={scenario.status === 'running' || isRunning}
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-500"
                          onClick={() => removeScenario(scenario.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
